import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { MdTitle, MdStore, MdLocationOn, MdLocalPostOffice } from "react-icons/md";
import Swal from 'sweetalert2';
import api from '../../../../hooks/api';
import { usePost } from '../../../../hooks/usePost';
import { updateBusinessLoginCache } from '../../../../hooks/auth/useBusinessLogin';
import './StoreInfo.css';

const customSelectStyles = {
    control: (provided) => ({
        ...provided,
        borderRadius: '15px',
        borderColor: '#ddd',
        height: '50px',
        fontFamily: 'vazir',
        fontSize: '14px',
        boxShadow: 'none',
        '&:hover': { borderColor: '#5693F5' }
    }),
    placeholder: (provided) => ({
        ...provided,
        fontFamily: 'vazir',
        fontSize: '13px'
    }),
    option: (provided) => ({
        ...provided,
        fontFamily: 'vazir',
        fontSize: '13px',
        textAlign: 'right'
    })
};

function StoreInfo({ businessHook }) {
    const { data: businessData, login: success, ChatId, collabId } = businessHook || {};
    const currentCollabId = collabId || null;

    const businessId = businessData?.id;
    const storeTitle = businessData?.title;
    const rawCategoryId = businessData?.category_id || businessData?.category?.id;

    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [Postcode, setPostcode] = useState('');
    const [Address, setAddress] = useState('');
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoriesList, setCategoriesList] = useState([]);
    const [disable, setDisable] = useState(false);

    const titleLenght = 40;
    const messageLenght = 70;
    const addressLength = 150;
    const postcodeLength = 10;

    const { data: ds_categories } = usePost(`category/all`, { rowCount: 100 });

    useEffect(() => {
        if (ds_categories?.data?.data) {
            setCategoriesList(ds_categories.data.data);
        }
    }, [ds_categories]);

    // 🔹 استخراج و ساخت آبجکت نهایی category حتی اگر سرور فقط category_id فرستاده باشد
    const category = useMemo(() => {
        if (businessData?.category && typeof businessData.category === 'object') {
            return businessData.category;
        }
        if (rawCategoryId && categoriesList.length > 0) {
            const found = categoriesList.find(c => Number(c.id) === Number(rawCategoryId));
            if (found) return found;
            return { id: rawCategoryId, title: `گروه انتخابی (#${rawCategoryId})` };
        }
        if (rawCategoryId) {
            return { id: rawCategoryId, title: "در حال دریافت عنوان گروه..." };
        }
        return null;
    }, [businessData, rawCategoryId, categoriesList]);

    const isUpdate = Boolean(category?.id || storeTitle);

    useEffect(() => {
        if (businessData) {
            setTitle(businessData.title || "");
            setMessage(businessData.message || "");
            setPostcode(businessData.post_code || "");
            setAddress(businessData.address || "");
        }
    }, [businessData]);

    const options = categoriesList ? categoriesList.map((item) => ({ value: item.id, label: item.title })) : [];

    const handleSubmit_Info = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setDisable(true);

        let groupId = null;
        let groupTitle = "";

        if (category && category.id) {
            groupId = category.id;
            groupTitle = category.title;
        } else if (currentCategory && currentCategory.value) {
            groupId = currentCategory.value;
            groupTitle = currentCategory.label;
        }

        if (!groupId) {
            toast.error("لطفا گروه شغلی خود را انتخاب کنید", { position: "top-right", autoClose: 2000, theme: "light", rtl: true });
            setDisable(false);
            return;
        }

        const finalTitle = title || storeTitle;
        if (!finalTitle) {
            toast.error("لطفا نام فروشگاه خود را وارد کنید", { position: "top-right", autoClose: 2000, theme: "light", rtl: true });
            setDisable(false);
            return;
        }

        if (!category?.id || !storeTitle) {
            const result = await Swal.fire({
                title: 'تأیید نهایی اطلاعات',
                html: `
                    <div style="font-size: 14px; line-height: 2.2; text-align: right; color: #4b5563; font-family: inherit;">
                        شما در حال ثبت <b style="color: #111827;">«گروه شغلی»</b> و <b style="color: #111827;">«نام فروشگاه»</b> هستید.
                        <div style="background-color: #fef2f2; color: #dc2626; padding: 12px; border-radius: 12px; margin-top: 15px; border: 1px solid #fecaca; font-size: 13px;">
                            <strong>⚠️ توجه:</strong> این اطلاعات پس از ثبت نهایی <strong>غیرقابل ویرایش</strong> خواهند بود.
                        </div>
                        <div style="margin-top: 15px; font-weight: bold; color: #1f2937;">
                            آیا از صحت اطلاعات اطمینان دارید؟
                        </div>
                    </div>
                `,
                icon: 'warning',
                iconColor: '#f59e0b',
                showCancelButton: true,
                confirmButtonText: 'بله، ثبت شود',
                cancelButtonText: 'بازگشت',
                reverseButtons: true,
                buttonsStyling: false,
                width: '90%',
                padding: '1.5em 1em',
                allowOutsideClick: false,
                customClass: {
                    popup: 'swal-custom-popup',
                    title: 'swal-custom-title',
                    actions: 'swal-custom-actions',
                    confirmButton: 'swal-btn-confirm',
                    cancelButton: 'swal-btn-cancel'
                }
            });

            if (!result.isConfirmed) {
                setDisable(false);
                return;
            }
        }

        const form = {
            category_id: groupId,
            title: finalTitle,
            message: message,
            post_code: Postcode,
            address: Address,
            business_id: businessId,
        };

        api.post(`business/info/store`, form)
            .then((response) => {
                setDisable(false);

                if (response.data.status || response.data.success) {
                    toast.success(isUpdate ? "اطلاعات فروشگاه با موفقیت بروزرسانی شد" : "اطلاعات فروشگاه با موفقیت ثبت شد", {
                        position: "top-right",
                        autoClose: 2000,
                        theme: "light",
                        rtl: true
                    });

                    const savedData = response.data.data || {};
                    const finalCategoryObj = (savedData.category && typeof savedData.category === 'object')
                        ? savedData.category
                        : { id: groupId, title: groupTitle };

                    const updatedPayload = {
                        ...(businessData || {}),
                        ...savedData,
                        id: businessId || savedData.id,
                        title: finalTitle,
                        message: message,
                        post_code: Postcode,
                        address: Address,
                        category: finalCategoryObj,
                        category_id: groupId,
                        businessStatus_id: 19
                    };

                    updateBusinessLoginCache(ChatId, currentCollabId, updatedPayload);

                    if (typeof businessHook?.refetch === 'function') {
                        businessHook.refetch();
                    } else if (typeof businessHook?.mutate === 'function') {
                        businessHook.mutate(updatedPayload, false);
                    }

                    window.dispatchEvent(new CustomEvent('businessInfoUpdated', {
                        detail: {
                            collabId: currentCollabId,
                            businessId: businessId || savedData.id,
                            data: updatedPayload
                        }
                    }));
                }
            })
            .catch((error) => {
                setDisable(false);
                if (error.response?.data?.message) {
                    toast.error(error.response.data.message, { position: "top-right", autoClose: 3000, theme: "light", rtl: true });
                } else {
                    toast.error("خطا در برقراری ارتباط با سرور", { position: "top-right", autoClose: 2000, theme: "light", rtl: true });
                }
            });
    };

    return (
        <div className="shop-info">
            {(!category?.id || !storeTitle) && (
                <div
                    className="alert-warning"
                    style={{
                        backgroundColor: "#fff3cd",
                        color: "#856404",
                        padding: "12px 15px",
                        borderRadius: "8px",
                        marginBottom: "20px",
                        border: "1px solid #ffeeba",
                        fontSize: "13px",
                        lineHeight: "1.7",
                        textAlign: "right"
                    }}
                >
                    <strong>⚠️ توجه:</strong> «گروه شغلی» و «نام فروشگاه» پس از ثبت <strong>قابل ویرایش نیستند</strong>. لطفاً دقت کنید.
                </div>
            )}

            <div className="title" style={{ marginTop: "10px" }}> انتخاب دسته بندی فروشگاه </div>
            {!category?.id ? (
                <div className="info-box" style={{ width: '100%', marginBottom: '10px' }}>
                    <Select
                        placeholder="جستجو و انتخاب گروه شغلی..."
                        options={options}
                        styles={customSelectStyles}
                        onChange={(choice) => setCurrentCategory(choice)}
                        isLoading={!categoriesList.length}
                        rtl={true}
                    />
                </div>
            ) : (
                <div className="input-box active locked">
                    <div className="text-center">{category.title}</div>
                    <div className="icon-right"><MdStore size={22} color="#4caf50" /></div>
                </div>
            )}
            <div className="description">فروشگاه شما در چه زمینه ای فعالیت دارد؟ از میان گزینه ها انتخاب کنید.</div>

            <div className="title section-gap">نام فروشگاه شما به فارسی</div>
            {!storeTitle ? (
                <div className={`input-box ${title && title.length > 0 ? "active" : ""}`}>
                    <div className='counter'>{(titleLenght - (title?.length || 0))}</div>
                    <div className="text-input-wrapper">
                        <input
                            type='text'
                            placeholder='مثال: پوشاک شیک'
                            maxLength={titleLenght}
                            value={title || ''}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>
                    <div className="icon"><MdTitle size={20} className="input-icon" /></div>
                </div>
            ) : (
                <div className="input-box active locked">
                    <div className="text-center">{storeTitle}</div>
                    <div className="icon-right"><MdTitle size={22} color="#4caf50" /></div>
                </div>
            )}
            <div className="description">یک نام کوتاه و فارسی برای نمایش در بالای فروشگاه خود انتخاب کنید.</div>

            <div className="title section-gap">پیام خوش آمدگویی فروشگاه</div>
            <div className={`input-box ${message && message.length > 0 ? "active" : ""}`}>
                <div className='counter'>{(messageLenght - (message?.length || 0))}</div>
                <div className="text-input-wrapper">
                    <input
                        type='text'
                        placeholder='مثال: به فروشگاه ما خوش آمدید'
                        maxLength={messageLenght}
                        value={message || ''}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                </div>
                <div className="icon"><MdTitle size={20} className="input-icon" /></div>
            </div>
            <div className="description">این پیام در هدر فروشگاه، زیر نام فروشگاه نمایش داده می‌شود.</div>

            <div className="title section-gap">کد پستی فروشگاه</div>
            <div className={`input-box ${Postcode && Postcode.length > 0 ? "active" : ""}`}>
                <div className='counter'>{(postcodeLength - (Postcode?.length || 0))}</div>
                <div className="text-input-wrapper">
                    <input
                        type='text'
                        dir='ltr'
                        placeholder='مثال: 1234567890'
                        maxLength={postcodeLength}
                        value={Postcode || ''}
                        onChange={(e) => setPostcode(e.target.value)}
                    />
                </div>
                <div className="icon"><MdLocalPostOffice size={20} className="input-icon" /></div>
            </div>
            <div className="description">کد پستی ۱۰ رقمی محل کسب یا انبار خود را وارد کنید.</div>

            <div className="title section-gap">آدرس فروشگاه</div>
            <div className={`input-box ${Address && Address.length > 0 ? "active" : ""}`}>
                <div className='counter'>{(addressLength - (Address?.length || 0))}</div>
                <div className="text-input-wrapper">
                    <input
                        type='text'
                        placeholder='مثال: تهران، خیابان آزادی، پلاک ۱۲'
                        maxLength={addressLength}
                        value={Address || ''}
                        onChange={(e) => setAddress(e.target.value)}
                    />
                </div>
                <div className="icon"><MdLocationOn size={20} className="input-icon" /></div>
            </div>
            <div className="description">آدرس دقیق فروشگاه، دفتر یا محل ارسال مرسولات را درج نمایید.</div>

            <div className={`save-info ${disable ? "disabled" : ""}`} onClick={!disable ? handleSubmit_Info : null}>
                {disable ? "در حال پردازش..." : (isUpdate ? "بروزرسانی اطلاعات" : "ثبت اطلاعات")}
            </div>
        </div>
    );
}

export default StoreInfo;
